import moment from "moment-timezone";
import { normalizeSchedulerOfficeId } from "accountOwner/pages/Scheduler/scheduler.function";
import {
  getLocalTimeZoneCode,
  hasTimeChanged,
} from "accountOwner/pages/AddEvent/addEvents.functions";
import { addEditEventsConst } from "accountOwner/pages/AddEvent/addEvents.constants";
import { editTypesVariables } from "accountOwner/pages/EditEvent/EditOptionsModal";
import {
  resolveEventOrShiftOffice,
  resolveOfficeIdFromName,
} from "./dragDropAssignment.utils";

/**
 * @param {object|null|undefined} eventData
 * @returns {object[]}
 */
export function getActiveEventEmployees(eventData) {
  return (eventData?.eventEmployees || []).filter(
    (employee) => !employee?.isDeletedForThisSchedulerEvent,
  );
}

/**
 * @param {object} employee
 * @returns {{ id: number|string|null, schedulerEventId: number|string|null, userId: number|string|null, officeId: number|string|null, IsDeleted: boolean, isDeleted: boolean }}
 */
export function mapEmployeeForPayload(employee, eventData) {
  return {
    ...employee,
    id: employee?.id,
    schedulerEventId: employee?.schedulerEventId ?? eventData?.id,
    userId: employee?.userId ?? employee?.user?.id,
    officeId:
      normalizeSchedulerOfficeId(employee?.officeId) ?? employee?.officeId,
    IsDeleted: false,
    isDeleted: false,
  };
}

/**
 * Maps office picker options from byOwnerAndUser (or similar) payloads.
 * @param {Array<{ id?: number|string, officeId?: number|string, name?: string, officeName?: string }>} officeList
 * @returns {Array<{ id: number, name: string }>}
 */
export function normalizeQuickEditOffices(officeList = []) {
  return (officeList || [])
    .map((office) => ({
      id: normalizeSchedulerOfficeId(office?.id ?? office?.officeId),
      name: office?.name || office?.officeName || "",
    }))
    .filter((office) => office.id);
}

/**
 * Resolves the office already assigned on a listing card for Quick Edit.
 * Listing rows often have a name without a root `officeId`.
 * @param {object} eventItem
 * @returns {{ officeId: number|null, officeName: string }}
 */
export function resolveQuickEditInitialOffice(eventItem) {
  const { officeId, officeName } = resolveEventOrShiftOffice({
    eventData: eventItem,
  });
  return {
    officeId,
    officeName: officeName || "",
  };
}

/**
 * Picks the selected office id from the loaded list: id first, then name.
 * Never drops a known preferred id just because the list has not caught up.
 * @param {{
 *   offices?: Array<{ id?: number|string, name?: string }>,
 *   preferredOfficeId?: number|string|null,
 *   preferredOfficeName?: string|null,
 * }} options
 * @returns {number|null}
 */
export function resolveQuickEditSelectedOfficeId({
  offices = [],
  preferredOfficeId,
  preferredOfficeName,
} = {}) {
  const preferred = normalizeSchedulerOfficeId(preferredOfficeId);
  if (
    preferred &&
    offices.some((office) => String(office?.id) === String(preferred))
  ) {
    return preferred;
  }

  const fromName = resolveOfficeIdFromName(offices, preferredOfficeName);
  if (fromName) return fromName;

  if (preferred) return preferred;

  if (offices.length === 1) {
    return normalizeSchedulerOfficeId(offices[0]?.id);
  }

  return null;
}

/**
 * Quick Edit always targets this occurrence for recurring series.
 * @param {object} eventData
 * @returns {number}
 */
export function resolveQuickEditType(eventData) {
  const repeatedType = eventData?.repeatedType || 1;
  const isNever =
    repeatedType === addEditEventsConst.repeatedOptionsValues.never ||
    repeatedType === 1;
  return isNever
    ? editTypesVariables.allEvent
    : editTypesVariables.singleEvent;
}

/**
 * @param {Date|string|moment.Moment} date
 * @param {moment.Moment|Date|string|null} time
 * @returns {string}
 */
export function combineDateAndTime(date, time) {
  if (!time) return null;
  return (
    moment(date).format("YYYY-MM-DDT") + moment(time).format("HH:mm")
  );
}

/**
 * @param {object} params
 * @param {object} params.eventData
 * @param {string|Date} params.requestedDate
 * @param {number|string|null} params.selectedUserId
 * @param {number|string} params.officeId
 * @param {Date|string} params.eventDate
 * @param {moment.Moment|Date|string} params.startTime
 * @param {moment.Moment|Date|string} params.endTime
 */
export function analyzeQuickEditChanges({
  eventData,
  requestedDate,
  selectedUserId,
  officeId,
  eventDate,
  startTime,
  endTime,
}) {
  const activeEmployees = getActiveEventEmployees(eventData).map((employee) =>
    mapEmployeeForPayload(employee, eventData),
  );
  const selectedEmployee =
    activeEmployees.find(
      (employee) =>
        selectedUserId != null &&
        String(employee.userId) === String(selectedUserId),
    ) || activeEmployees[0];

  const currentOfficeId =
    normalizeSchedulerOfficeId(selectedEmployee?.officeId) ??
    normalizeSchedulerOfficeId(eventData?.officeId);
  const nextOfficeId = normalizeSchedulerOfficeId(officeId);

  const newStartTimeString = combineDateAndTime(eventDate, startTime);
  const newEndTimeString = combineDateAndTime(eventDate, endTime);

  const isOfficeChanged =
    currentOfficeId != null &&
    nextOfficeId != null &&
    String(currentOfficeId) !== String(nextOfficeId);

  const isDateChanged = !moment(requestedDate)
    .startOf("day")
    .isSame(moment(eventDate).startOf("day"));

  const isStartTimeChanged = hasTimeChanged(
    eventData?.startTime,
    newStartTimeString,
  );
  const isEndTimeChanged = hasTimeChanged(eventData?.endTime, newEndTimeString);

  const hasDateTimeChange =
    isDateChanged || isStartTimeChanged || isEndTimeChanged;

  const isMultiAssignee =
    selectedUserId != null &&
    activeEmployees.filter((employee) => employee?.userId != null).length > 1;

  return {
    activeEmployees,
    selectedEmployee,
    currentOfficeId,
    nextOfficeId,
    newStartTimeString,
    newEndTimeString,
    isOfficeChanged,
    isDateChanged,
    isStartTimeChanged,
    isEndTimeChanged,
    hasDateTimeChange,
    isMultiAssignee,
    shouldSplit: Boolean(isMultiAssignee && hasDateTimeChange),
  };
}

/**
 * Builds PUT payload for in-place Quick Edit (single assignee, or office-only on multi).
 */
export function buildQuickEditUpdateParams({
  eventData,
  ownerId,
  requestedDate,
  eventDate,
  selectedUserId,
  analysis,
  keepOriginalDateTime = false,
  removeSelectedUser = false,
}) {
  const editType = resolveQuickEditType(eventData);
  const originalRequestedDate = moment(requestedDate).format("YYYY-MM-DDTHH:mm");

  let eventEmployeesPayload = analysis.activeEmployees.map((employee) => ({
    ...employee,
  }));

  let removedEmployeeRowIds = [];
  let touchedEmployeeUserIds = [];

  if (removeSelectedUser && selectedUserId != null) {
    const removed = eventEmployeesPayload.filter(
      (employee) => String(employee.userId) === String(selectedUserId),
    );
    removedEmployeeRowIds = removed
      .map((employee) => employee.id)
      .filter((id) => id != null && id !== 0);
    touchedEmployeeUserIds = [selectedUserId];
    eventEmployeesPayload = eventEmployeesPayload.filter(
      (employee) => String(employee.userId) !== String(selectedUserId),
    );
  } else if (analysis.isOfficeChanged && selectedUserId != null) {
    const selectedRows = eventEmployeesPayload.filter(
      (employee) => String(employee.userId) === String(selectedUserId),
    );
    removedEmployeeRowIds = selectedRows
      .map((employee) => employee.id)
      .filter((id) => id != null && id !== 0);
    touchedEmployeeUserIds = [selectedUserId];
    const template = selectedRows[0] || analysis.selectedEmployee || {};
    eventEmployeesPayload = [
      ...eventEmployeesPayload.filter(
        (employee) => String(employee.userId) !== String(selectedUserId),
      ),
      {
        ...template,
        id: 0,
        schedulerEventId: template?.schedulerEventId ?? eventData?.id,
        userId: selectedUserId,
        officeId: analysis.nextOfficeId,
        IsDeleted: false,
        isDeleted: false,
      },
    ];
  }

  const startTimeString = keepOriginalDateTime
    ? moment(eventData.startTime).format("YYYY-MM-DDTHH:mm")
    : analysis.newStartTimeString;
  const endTimeString = keepOriginalDateTime
    ? moment(eventData.endTime).format("YYYY-MM-DDTHH:mm")
    : analysis.newEndTimeString;
  const dateForParams = keepOriginalDateTime
    ? moment(eventData?.date || requestedDate).format("YYYY-MM-DDTHH:mm")
    : moment(
        moment(requestedDate)
          .startOf("day")
          .isSame(moment(eventDate).startOf("day"))
          ? eventData?.date || eventDate
          : eventDate,
      ).format("YYYY-MM-DDTHH:mm");

  const isShiftOfficeChange = Boolean(
    eventData?.isShift &&
      analysis.isOfficeChanged &&
      !removeSelectedUser &&
      !analysis.isMultiAssignee,
  );

  const params = {
    SchedulerEventId: eventData.id,
    OwnerId: ownerId,
    Title: eventData.title || "",
    Date: dateForParams,
    StartTime: startTimeString,
    EndTime: endTimeString,
    IsAllDayEvent: keepOriginalDateTime
      ? Boolean(eventData.isAllDayEvent)
      : false,
    RepeatedType: eventData.repeatedType || 1,
    RepeatedDay: moment(
      keepOriginalDateTime ? requestedDate : eventDate,
    ).isoWeekday(),
    RepeatedEndDate:
      eventData.repeatedType > 1 && eventData.repeatedEndDate
        ? moment(eventData.repeatedEndDate).format("YYYY-MM-DD")
        : null,
    Location: eventData.location || "",
    BlockForPatient: eventData.blockForPatient || false,
    Note: eventData.note || "",
    PublishAsEvent: eventData.publishAsEvent || false,
    eventEmployees: eventEmployeesPayload,
    EventRoles: Array.isArray(eventData.eventRoles) ? eventData.eventRoles : [],
    EventTags: Array.isArray(eventData.eventTags) ? eventData.eventTags : [],
    CreatedAt: moment().format("YYYY-MM-DDTHH:mm"),
    ReminderBeforeInMins: eventData.reminderBeforeInMins || 0,
    isDraft: eventData.isDraft || false,
    requestedDate: originalRequestedDate,
    timezoneCode: eventData.timezoneCode || moment.tz.guess(),
    EditType: editType,
    IsStaffView: true,
    UserId: selectedUserId ?? null,
    UpdateFlags: {
      isTitleEdit: false,
      isStartTimeEdit: keepOriginalDateTime
        ? false
        : analysis.isStartTimeChanged,
      isEndTimeEdit: keepOriginalDateTime ? false : analysis.isEndTimeChanged,
      isAllDayEventEdit: Boolean(
        !keepOriginalDateTime && eventData.isAllDayEvent,
      ),
      isNoteEdit: false,
      isLocationEdit: false,
      isReminderBeforeInMinsEdit: false,
      isEnterpriseEdit: false,
      isOfficeIdEdit: false,
      EmployeeIds: touchedEmployeeUserIds,
      RemovedEmployeeRowIds: removedEmployeeRowIds,
      IsShiftOfficeChange: isShiftOfficeChange,
      RoleIds: [],
      IsRepeatTypeChange: false,
      IsEventDateChange: keepOriginalDateTime ? false : analysis.isDateChanged,
      IsRepeatedEndDateChange: false,
      IsBlockForPatientChange: false,
      tagsIds: false,
    },
  };

  if (eventData.isShift) {
    params.Title = eventData.title || "";
    delete params.Location;
    delete params.Note;
    const keepSharedShiftOffice =
      analysis.isMultiAssignee && !removeSelectedUser;
    const shiftOfficeId = keepSharedShiftOffice
      ? normalizeSchedulerOfficeId(eventData?.officeId) ??
        normalizeSchedulerOfficeId(eventEmployeesPayload?.[0]?.officeId)
      : analysis.nextOfficeId ??
        normalizeSchedulerOfficeId(eventEmployeesPayload?.[0]?.officeId) ??
        normalizeSchedulerOfficeId(eventData?.officeId);
    if (shiftOfficeId) {
      params.officeId = shiftOfficeId;
    }
  } else {
    params.IsAutoApprove = eventData.isAutoApprove || false;
  }

  return params;
}

/**
 * Builds POST payload when Quick Edit must fork one user off a shared event.
 */
export function buildQuickEditCreateParams({
  eventData,
  ownerId,
  selectedUserId,
  analysis,
  eventDate,
}) {
  const timeZone = getLocalTimeZoneCode();
  const selectedEmployee =
    analysis.selectedEmployee ||
    analysis.activeEmployees.find(
      (employee) => String(employee.userId) === String(selectedUserId),
    );

  const eventEmployees = [
    {
      ...(selectedEmployee || {}),
      id: 0,
      schedulerEventId: 0,
      userId: selectedUserId,
      officeId: analysis.nextOfficeId,
      IsDeleted: false,
      isDeleted: false,
    },
  ];

  const params = {
    OwnerId: ownerId,
    Title: eventData.title || "",
    Date: moment(eventDate).format("YYYY-MM-DDTHH:mm"),
    StartTime: analysis.newStartTimeString,
    EndTime: analysis.newEndTimeString,
    IsAllDayEvent: false,
    RepeatedType: addEditEventsConst.repeatedOptionsValues.never,
    isShift: Boolean(eventData.isShift),
    RepeatedDay: moment(eventDate).isoWeekday(),
    RepeatedEndDate: null,
    Location: eventData.location || "",
    Note: eventData.note || "",
    PublishAsEvent: eventData.publishAsEvent || false,
    isDraft: eventData.isDraft || false,
    eventEmployees,
    EventRoles: Array.isArray(eventData.eventRoles) ? eventData.eventRoles : [],
    EventTags: eventData.isShift
      ? []
      : Array.isArray(eventData.eventTags)
        ? eventData.eventTags
        : [],
    CreatedAt: moment().format("YYYY-MM-DDTHH:mm"),
    ReminderBeforeInMins: eventData.reminderBeforeInMins || 0,
    UtcOffsetInMinutes: moment().utcOffset(),
    BlockForPatient: eventData.isShift
      ? false
      : eventData.blockForPatient || false,
    timezoneCode: eventData.timezoneCode || timeZone.code,
    timezoneId: timeZone.timeZoneId,
    IsExcludeLeave: false,
    IsExcludeHoliday: false,
  };

  if (eventData.isShift) {
    delete params.Title;
    delete params.Location;
    delete params.Note;
    params.officeId = analysis.nextOfficeId;
  } else {
    params.IsAutoApprove = eventData.isAutoApprove || false;
  }

  return params;
}
